// Provider stack
import { AppToastProvider } from "#app/components/app-toast-provider";
import { AnalyticsProvider } from "#app/lib/analytics";
import { DataProvider } from "#app/lib/data/data-provider";
import { AppProvider } from "#app/lib/data/app-provider";
import { MemberDirectoryProvider } from "#app/lib/data/member-directory-provider";
import { PresenceProvider } from "#app/lib/data/presence-provider";
import { WorkspaceDataProvider } from "#app/lib/data/workspace-data-provider";
import { SubscriptionProvider } from "#app/lib/subscriptions/subscription-provider";
import { composeProviders } from "#app/lib/compose-providers";
import { EntityDetailProvider } from "#app/components/entity";
import { GlobalSearchProvider } from "#app/components/global-search/global-search-context";
import { ModuleRailProvider } from "#app/components/navigation/module-rail";
import { ThemeAppearanceProvider } from "#app/components/theme/theme-appearance-provider";
import { IssueViewProvider } from "#app/components/issue-view";
import React from "react";

// IMPORTANT — a Base UI Portal moves only the DOM node: React context reaches
// drawer/dialog content from where the overlay is RENDERED in the tree. Any
// Provider an overlay's content calls must sit ABOVE the component that
// renders that overlay.
// Overlay-mounting components live in the workspace layout, below this whole
// stack — so keep data/state providers here and render overlays there.
//
// COROLLARY: providers listed here must NOT render a `Drawer` (or a
// `useIsMobile()` branch that renders one) inline. Split context from
// render: keep the Provider here, and render the overlay inside the
// workspace layout. See `IssueViewDialogs` / `CustomFieldsDialog` for the
// pattern.

export const AppProviders = composeProviders([
  [AnalyticsProvider],
  [DataProvider],
  // Apollo-dependent (reads the accent + neutral-shift preferences) → must sit
  // inside DataProvider. High in the stack so the custom properties it writes
  // to <html> are on the document before anything below it paints.
  [ThemeAppearanceProvider],
  [AppProvider],
  // Ref-counted registry for every GraphQL subscription (needs the Apollo
  // client from DataProvider). Anything using useRegisterSubscriptions sits
  // below this.
  [SubscriptionProvider],
  [WorkspaceDataProvider],
  // Reads useWorkspaceData (members to poll) — must sit below it. Every
  // user avatar (EntityVisual) reads the statuses it provides.
  [PresenceProvider],
  // Also reads useWorkspaceData (to skip ids the bootstrap page already
  // answers) → below it. Resolves member ids OUTSIDE that page, so anything
  // that words a member id sits below this.
  [MemberDirectoryProvider],
  [IssueViewProvider],
  // Must sit below IssueViewProvider (delegates issues to it) and above
  // anything that renders a portaled overlay calling it.
  [EntityDetailProvider],
  [GlobalSearchProvider],
  [ModuleRailProvider],
  // Apollo-dependent (reads the toastPosition preference) → must sit inside
  // DataProvider. Mounts the ONE toast viewport for the whole app.
  [AppToastProvider],
]);
